/**
 * Groundhog Day Service
 * Generates booking data with the same guests in the same areas across multiple days
 */

import { ViewBooking, ViewBookingStatusEnum } from "collins-js-sdk";
import dayjs from "dayjs";
import {
  TEMPLATE_GUESTS,
  TemplateGuest,
  getRandomStatus,
  getRandomStage,
  getTimeWithVariation,
  generateId
} from "./templateData";
import { addOrUpdateBooking } from "../../APIs/Booking/UpdateBooking";
import { DiaryContextType } from "../../context/DiaryContext";
import { BookableArea, BookingStage, BookingType } from "../../types/venues";

type BookingTemplateConfig = {
  guest: TemplateGuest;
  date: Date;
  venueId: string;
  venueGroup: string;
  bookingTypes: BookingType[];
  bookableAreas: BookableArea[];
  bookingStages: BookingStage[];
  options: GenerationOptions;
};

export type GenerationOptions = {
  randomiseStates: boolean;
  includeDeposits: boolean;
  includePreorders: boolean;
  includeDietaryRequirements: boolean;
  includeNotes: boolean;
  includeComments: boolean;
  includeSmsEmails: boolean;
};

export type GenerationProgress = {
  current: number;
  total: number;
  currentDate: string;
  currentGuest: string;
  status: "pending" | "generating" | "complete" | "error";
  error?: string;
};

const DEFAULT_OPTIONS: GenerationOptions = {
  randomiseStates: true,
  includeDeposits: true,
  includePreorders: true,
  includeDietaryRequirements: true,
  includeNotes: true,
  includeComments: true,
  includeSmsEmails: true
};

/**
 * Generate bookings for a date range using template guests
 */
export const generateGroundhogDayBookings = async (
  diaryContext: DiaryContextType,
  startDate: Date,
  endDate: Date,
  options: Partial<GenerationOptions> = {},
  onProgress?: (progress: GenerationProgress) => void
): Promise<{ success: boolean; bookingsCreated: number; errors: string[] }> => {
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options };
  const errors: string[] = [];
  let bookingsCreated = 0;

  const venueConfig = diaryContext.venueConfig;
  if (!venueConfig) {
    return {
      success: false,
      bookingsCreated: 0,
      errors: ["No venue configuration found"]
    };
  }

  const bookingTypes = venueConfig.booking_types ?? [];
  const bookableAreas = venueConfig.bookable_areas ?? [];
  const bookingStages = venueConfig.booking_stages ?? [];

  if (bookingTypes.length === 0) {
    return {
      success: false,
      bookingsCreated: 0,
      errors: ["No booking types configured for this venue"]
    };
  }

  const currentDate = dayjs(startDate);
  const end = dayjs(endDate);
  const totalDays = end.diff(currentDate, "day") + 1;
  const totalBookings = totalDays * TEMPLATE_GUESTS.length;

  let current = 0;

  // Iterate through each day
  let iterDate = currentDate;
  while (iterDate.isBefore(end) || iterDate.isSame(end, "day")) {
    // Generate bookings for each template guest
    for (const guest of TEMPLATE_GUESTS) {
      current++;

      onProgress?.({
        current,
        total: totalBookings,
        currentDate: iterDate.format("YYYY-MM-DD"),
        currentGuest: `${guest.firstName} ${guest.lastName}`,
        status: "generating"
      });

      try {
        const booking = createBookingFromTemplate({
          guest,
          date: iterDate.toDate(),
          venueId: venueConfig._id ?? "",
          venueGroup: venueConfig.venue_group ?? "",
          bookingTypes,
          bookableAreas,
          bookingStages,
          options: mergedOptions
        });

        await addOrUpdateBooking({ booking, diaryContext });
        bookingsCreated++;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        errors.push(
          `Failed to create booking for ${guest.firstName} ${guest.lastName} on ${iterDate.format("YYYY-MM-DD")}: ${errorMessage}`
        );
      }
    }

    iterDate = iterDate.add(1, "day");
  }

  onProgress?.({
    current: totalBookings,
    total: totalBookings,
    currentDate: end.format("YYYY-MM-DD"),
    currentGuest: "",
    status: errors.length === 0 ? "complete" : "error",
    error: errors.length > 0 ? `${errors.length} errors occurred` : undefined
  });

  return {
    success: errors.length === 0,
    bookingsCreated,
    errors
  };
};

/**
 * Determine booking status and stage based on guest category and options
 */
const determineBookingStatusAndStage = (
  guest: TemplateGuest,
  bookingStages: BookingStage[],
  options: GenerationOptions
): {
  status: ViewBookingStatusEnum;
  currentStage: string | undefined;
  walkIn: boolean;
  waitlisted: boolean;
} => {
  if (guest.category === "walkin") {
    return {
      status: ViewBookingStatusEnum.Complete,
      currentStage: bookingStages.find((s) => s.walk_in_default)?.id,
      walkIn: true,
      waitlisted: false
    };
  }

  if (guest.category === "waitlisted") {
    return {
      status: ViewBookingStatusEnum.InProgress,
      currentStage: undefined,
      walkIn: false,
      waitlisted: true
    };
  }

  if (options.randomiseStates) {
    const randomStatus = getRandomStatus();
    return {
      status: mapRandomStatusToEnum(randomStatus),
      currentStage: getRandomStage(
        randomStatus,
        bookingStages.map((s) => s.id)
      ),
      walkIn: false,
      waitlisted: false
    };
  }

  return {
    status: ViewBookingStatusEnum.Complete,
    currentStage: bookingStages.find(
      (s) => s.label?.toLowerCase() === "confirmed"
    )?.id,
    walkIn: false,
    waitlisted: false
  };
};

/**
 * Map random status string to ViewBookingStatusEnum
 */
const mapRandomStatusToEnum = (randomStatus: string): ViewBookingStatusEnum => {
  if (randomStatus === "new") {
    return ViewBookingStatusEnum.New;
  }
  if (randomStatus === "complete" || randomStatus === "confirmed") {
    return ViewBookingStatusEnum.Complete;
  }
  return ViewBookingStatusEnum.InProgress;
};

/**
 * Add deposits to booking if enabled
 */
const addDepositsToBooking = (
  booking: ViewBooking,
  guest: TemplateGuest,
  options: GenerationOptions
): void => {
  if (!options.includeDeposits || !guest.hasDeposit || !guest.depositAmount) {
    return;
  }

  const depositStatuses = ["paid", "pending", "authorised"];
  const depositStatus = options.randomiseStates
    ? depositStatuses[Math.floor(Math.random() * depositStatuses.length)]
    : "paid";

  (booking as any).deposits = [
    {
      id: generateId(),
      amount: guest.depositAmount,
      status: depositStatus,
      type: "request_payment",
      currency: "GBP",
      date: new Date(),
      emailReceipt: true,
      paymentFor: "deposit",
      paymentRef: `DEP-${generateId()}`,
      nicedate: dayjs().format("DD MMM YYYY"),
      claims: [],
      refundIds: null,
      refunds: null,
      depositFor: null,
      pushedToEpos: false,
      corporateCardFee: 0
    }
  ];
};

/**
 * Add preorders to booking if enabled
 */
const addPreordersToBooking = (
  booking: ViewBooking,
  guest: TemplateGuest,
  options: GenerationOptions
): void => {
  if (!options.includePreorders || !guest.hasPreorder || !guest.preorderItems) {
    return;
  }

  const totalCost = guest.preorderItems.reduce(
    (sum, item) => sum + item.total,
    0
  );
  const serviceChargePercentage = 12.5;
  const serviceChargeAmount = Math.round(
    totalCost * (serviceChargePercentage / 100)
  );
  const totalWithService = totalCost + serviceChargeAmount;

  const amountPaid = calculateAmountPaid(totalWithService, options);
  const preorderStatus = calculatePreorderStatus(options);

  (booking as any).preorders = [
    {
      id: generateId(),
      createdDate: new Date(),
      name: guest.firstName,
      email: guest.email,
      items: guest.preorderItems.map((item) => ({
        ...item,
        id: generateId()
      })),
      totalCost: totalWithService,
      amountPaid,
      source: "Admin",
      status: preorderStatus,
      serviceChargePercentage,
      serviceChargeAmount
    }
  ];
};

/**
 * Calculate amount paid for preorder
 */
const calculateAmountPaid = (
  totalWithService: number,
  options: GenerationOptions
): number => {
  if (!options.randomiseStates) {
    return totalWithService;
  }
  return Math.random() > 0.5 ? totalWithService : 0;
};

/**
 * Calculate preorder status
 */
const calculatePreorderStatus = (options: GenerationOptions): string => {
  if (!options.randomiseStates) {
    return "confirmed";
  }
  return Math.random() > 0.5 ? "confirmed" : "pending";
};

/**
 * Add SMS/Email data to booking if enabled
 */
const addCommunicationDataToBooking = (
  booking: ViewBooking,
  date: Date,
  status: ViewBookingStatusEnum,
  options: GenerationOptions
): void => {
  if (!options.includeSmsEmails || status !== ViewBookingStatusEnum.Complete) {
    return;
  }

  (booking as any).emailsSent = {
    confirmation: dayjs(date).subtract(1, "day").toDate()
  };

  if (Math.random() > 0.5) {
    (booking as any).smsSent = {
      general: dayjs(date).subtract(1, "day").toDate()
    };
  }

  booking.doubleConfirmed = Math.random() > 0.3;
};

/**
 * Add stage times for completed bookings
 */
const addStageTimes = (
  booking: ViewBooking,
  date: Date,
  status: ViewBookingStatusEnum,
  walkIn: boolean,
  waitlisted: boolean,
  duration: number
): void => {
  if (status !== ViewBookingStatusEnum.Complete || walkIn || waitlisted) {
    return;
  }

  const bookingTime = dayjs(date)
    .hour(parseInt(booking.time?.split(":")[0] ?? "19"))
    .minute(parseInt(booking.time?.split(":")[1] ?? "0"));

  (booking as any).stageTimes = {
    arrived: bookingTime.toDate(),
    seated: bookingTime.add(5, "minute").toDate(),
    left: bookingTime.add(duration, "minute").toDate()
  };
};

/**
 * Create a booking from a template guest
 */
const createBookingFromTemplate = (
  config: BookingTemplateConfig
): ViewBooking => {
  const {
    guest,
    date,
    venueId,
    venueGroup,
    bookingTypes,
    bookableAreas,
    bookingStages,
    options
  } = config;

  // Select a random booking type
  const bookingType =
    bookingTypes[Math.floor(Math.random() * bookingTypes.length)];

  // Select appropriate areas based on party size
  const suitableAreas = bookableAreas.filter(
    (area) =>
      (area.min_people ?? 1) <= guest.numPeople &&
      (area.max_people ?? 100) >= guest.numPeople
  );
  const selectedArea =
    suitableAreas.length > 0
      ? suitableAreas[Math.floor(Math.random() * suitableAreas.length)]
      : bookableAreas[0];

  // Determine status and stage using helper function
  const { status, currentStage, walkIn, waitlisted } =
    determineBookingStatusAndStage(guest, bookingStages, options);

  // Build the booking object
  const booking: ViewBooking = {
    id: undefined, // Will be assigned by the API
    venueId,
    venueGroup,
    date: date,
    time: getTimeWithVariation(guest.preferredTime),
    duration: guest.duration,
    numPeople: guest.numPeople,
    actualGuests: guest.numPeople,
    firstName: guest.firstName,
    lastName: guest.lastName,
    email: guest.email,
    phone: guest.phone,
    company: guest.company,
    status,
    currentStage,
    walkIn,
    waitlisted,
    type: {
      id: bookingType.id,
      name: bookingType.name,
      guestlist: bookingType.guestlist ?? false,
      value: bookingType.value ?? 0,
      privateHire: bookingType.private_hire ?? false
    },
    assignedAreas: selectedArea
      ? ([{ id: selectedArea.id, name: selectedArea.name ?? "" }] as any)
      : []
  };

  // Set properties not in ViewBooking type but needed by API
  (booking as any).source = "admin";
  (booking as any).managedByVenue = true;

  // Add optional data based on options
  if (options.includeNotes && guest.notes) {
    booking.notes = guest.notes;
  }

  if (options.includeDietaryRequirements && guest.dietaryRequirements) {
    booking.dietaryRequirements = guest.dietaryRequirements;
  }

  if (options.includeComments && guest.comments) {
    booking.comments = guest.comments.map((c) => ({
      ...c,
      time: new Date().toISOString()
    }));
  }

  if (guest.adminNotes) {
    (booking as any).adminNotes = guest.adminNotes.map((n) => ({
      ...n,
      time: new Date()
    }));
  }

  if (guest.labels) {
    booking.labels = guest.labels;
  }

  if (guest.customerLabels) {
    booking.customer_labels = guest.customerLabels;
  }

  if (guest.tags) {
    booking.tags = guest.tags;
  }

  if (guest.groupMembers) {
    (booking as any).groupMembers = guest.groupMembers.map((m) => ({
      ...m,
      id: generateId()
    }));
  }

  if (guest.minSpend) {
    (booking as any).minSpend = {
      amount: guest.minSpend.amount,
      perHead: guest.minSpend.perHead,
      paidAmount: 0
    };
  }

  // Add deposits, preorders, communication data, and stage times using helper functions
  addDepositsToBooking(booking, guest, options);
  addPreordersToBooking(booking, guest, options);
  addCommunicationDataToBooking(booking, date, status, options);
  addStageTimes(booking, date, status, walkIn, waitlisted, guest.duration);

  return booking;
};

export default {
  generateGroundhogDayBookings
};
